import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';

export interface AlarmSettings {
  enabled: boolean;
  minValue?: number;
  maxValue?: number;
  minDuration?: number;
  maxDuration?: number;
}

export interface AlarmData {
  id: string;
  sensorId: string;
  timestamp: string;
  value: number;
  type: 'min' | 'max';
  acknowledged: boolean;
  settings: AlarmSettings;
}

// Use the correct base URL for alarms
const alarmBaseQuery = createBaseQuery('https://alarms.ts1cloud.com');

export const alarmApi = createApi({
  reducerPath: 'alarmApi',
  baseQuery: alarmBaseQuery,
  tagTypes: ['Alarms', 'AlarmLog', 'RemoteAlertGroups'],
  endpoints: (builder) => ({
    getAlarms: builder.query<AlarmData[], void>({
      query: () => ({
        url: '/alarms',
        method: 'GET',
      }),
      providesTags: ['Alarms'],
    }),

    getSensorAlarm: builder.query<AlarmData, string>({
      query: (sensorId) => ({
        url: `/alarms/sensor/${sensorId}`,
        method: 'GET',
      }),
      providesTags: (result, error, sensorId) => [{ type: 'Alarms', id: sensorId }],
    }),

    updateSensorAlarm: builder.mutation<void, { sensorId: string; settings: AlarmSettings }>({
      query: ({ sensorId, settings }) => ({
        url: `/alarms/sensor/${sensorId}`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: (result, error, { sensorId }) => [{ type: 'Alarms', id: sensorId }],
    }),

    deleteSensorAlarm: builder.mutation<void, string>({
      query: (sensorId) => ({
        url: `/alarms/sensor/${sensorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, sensorId) => [{ type: 'Alarms', id: sensorId }],
    }),

    getAlarmLog: builder.query<any, { includeAcknowledged?: boolean } | void>({
      query: (params) => ({
        url: '/alarms/alarm-log',
        method: 'GET',
        params: params && 'includeAcknowledged' in params && params.includeAcknowledged ? { include_acknowledged: 'true' } : {},
      }),
      providesTags: ['AlarmLog'],
    }),

    acknowledgeAlarmLog: builder.mutation<any, { logId: number, body: any }>({
      query: ({ logId, body }) => ({
        url: `/alarms/alarm-log/${logId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AlarmLog'],
    }),

    acknowledgeByEventId: builder.mutation<any, { eventId: string; body: any }>({
      query: ({ eventId, body }) => ({
        url: `/alarms/alarm-log/event/${eventId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AlarmLog'],
    }),

    getRemoteAlertGroups: builder.query<any, void>({
      query: () => ({
        url: '/remote-alert-groups',
        method: 'GET',
      }),
      providesTags: ['RemoteAlertGroups'],
    }),
  }),
});

export const {
  useGetAlarmsQuery,
  useGetSensorAlarmQuery,
  useUpdateSensorAlarmMutation,
  useDeleteSensorAlarmMutation,
  useGetAlarmLogQuery,
  useAcknowledgeAlarmLogMutation,
  useAcknowledgeByEventIdMutation,
  useGetRemoteAlertGroupsQuery,
} = alarmApi; 